import React from 'react';
import AIResponseLoader from '../common/AIResponseLoader';
import AIResponseError from '../common/AIResponseError';
import { getData } from '../../DataAccessLayer';
import { quizzes } from '../../DataAccessLayer/services';
import { RESOURCE_STATUS, RESOURCE_ERROR_MESSAGES } from '../common/constants';

/**
 * What to show while a quiz is still being generated, or once generation has
 * failed. Renders nothing for a finished quiz, so the page can carry straight on
 * to its own content.
 *
 * Props:
 *   quizId          (string)   - The quiz being generated.
 *   courseId        (string)   - The course the quiz belongs to.
 *   status          (string)   - One of RESOURCE_STATUS.
 *   errorCode       (string)   - Why generation failed, when the backend could tell.
 *   onGenerated     (function) - Called with the finished quiz once polling completes.
 *   onGenerateNew   (function) - Take the learner back to the generator.
 *   onGoToDashboard (function) - Take the learner back to the dashboard.
 */
export const QuizGenerationStatus = ({
  quizId,
  courseId,
  status,
  errorCode,
  onGenerated,
  onGenerateNew,
  onGoToDashboard,
}) => {
  if (status === RESOURCE_STATUS.IN_PROGRESS) {
    return (
      <AIResponseLoader
        poll={() => getData(quizzes({ id: quizId, courseId }))}
        isComplete={(data) => data.status === RESOURCE_STATUS.COMPLETED}
        // Generation can also end in failure, which stops the polling just the
        // same — without this the loader would spin forever on a failed quiz.
        isErrored={(data) => data.status === RESOURCE_STATUS.ERROR}
        onComplete={onGenerated}
        resource="Quiz"
        onGenerateNew={onGenerateNew}
        onGoToDashboard={onGoToDashboard}
      />
    );
  }

  if (status === RESOURCE_STATUS.ERROR) {
    return (
      <AIResponseError
        message={
          RESOURCE_ERROR_MESSAGES[errorCode]?.('practice quiz') ??
          'Practice quiz generation failed.'
        }
        resource="Quiz"
        onGenerateNew={onGenerateNew}
        onGoToDashboard={onGoToDashboard}
      />
    );
  }

  return null;
};
